import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle2, Gamepad2, ShoppingBag, Truck, Package, Clock, XCircle, Copy } from 'lucide-react';
import { publicApi } from '../api';
import { toast } from '../hooks/use-toast';

const STATUS_META = {
  pending:    { label: 'Order Placed',   color: '#F4A300', icon: Clock,        desc: 'We\u2019ve got your order and will confirm soon.' },
  confirmed:  { label: 'Confirmed',      color: '#2A9D8F', icon: CheckCircle2, desc: 'Order confirmed. Our kitchen is on it.' },
  dispatched: { label: 'Dispatched',     color: '#4A90E2', icon: Truck,        desc: 'Your crunch is on the way!' },
  delivered:  { label: 'Delivered',      color: '#2A9D8F', icon: Package,      desc: 'Enjoy every bite. Tag us @trucrunch!' },
  cancelled:  { label: 'Cancelled',      color: '#E63946', icon: XCircle,      desc: 'This order was cancelled.' },
};

const OrderSuccess = () => {
  const { id } = useParams();
  const location = useLocation();
  const stateEmail = location.state?.email;
  const stateTotal = location.state?.total;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!stateEmail);

  useEffect(() => {
    if (!stateEmail) return;
    publicApi.trackOrder(id, stateEmail).then(setOrder).catch(() => {}).finally(() => setLoading(false));
  }, [id, stateEmail]);

  const meta = STATUS_META[order?.status || 'pending'];
  const Icon = meta.icon;

  const copyId = () => {
    navigator.clipboard.writeText(id);
    toast({ title: 'Copied!', description: 'Order ID copied to clipboard' });
  };

  return (
    <div className="bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <section className="px-5 md:px-8 pt-32 pb-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2A9D8F] text-white px-4 py-2 rounded-full font-brico font-bold text-sm uppercase tracking-widest">
            <CheckCircle2 size={16} /> Order Placed
          </div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-4">
            Big crunch<br />incoming!
          </h1>
          <p className="font-brico text-lg mt-4 opacity-80">
            Thanks for ordering. We'll reach out to <span className="font-bold">{stateEmail || 'you'}</span> with confirmation and delivery details.
          </p>

          {/* Order card */}
          <div className="mt-8 bg-white rounded-3xl crunch-shadow p-6 md:p-8 text-left">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest opacity-70">Order ID</div>
                <button onClick={copyId} className="font-display text-lg md:text-xl mt-1 break-all inline-flex items-center gap-2 hover:text-[#E63946]">
                  {id} <Copy size={14} />
                </button>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-brico font-bold text-xs uppercase tracking-widest flex-shrink-0" style={{ background: meta.color }}>
                <Icon size={14} /> {meta.label}
              </div>
            </div>

            <p className="font-brico mt-3 opacity-80">{meta.desc}</p>

            {order?.tracking_id && (
              <div className="mt-5 bg-[#FFF4E6] rounded-2xl p-4 border-2 border-dashed border-[#1a1a1a]/20">
                <div className="text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2"><Truck size={12} /> Tracking</div>
                <div className="mt-1 font-display text-xl">{order.courier}</div>
                <div className="font-mono text-sm mt-1">{order.tracking_id}</div>
              </div>
            )}

            {(stateTotal !== undefined || order) && (
              <div className="mt-5 pt-4 border-t-2 border-black/10">
                <div className="text-xs font-bold uppercase tracking-widest opacity-70">Total</div>
                <div className="font-display text-4xl">₹{order?.total ?? stateTotal}</div>
              </div>
            )}
          </div>

          <div className="mt-6 text-xs font-brico opacity-70">
            Save your Order ID. You can{' '}
            <Link to="/track" className="underline font-bold">track your order</Link> anytime with your email.
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/shop" className="btn-chunky outline"><ShoppingBag size={16} /> Shop More</Link>
            <Link to="/play" className="btn-chunky primary"><Gamepad2 size={16} /> Play the Game</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default OrderSuccess;
